import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import Landing from './pages/Landing';
import {
  AgeVerificationPage,
  AmlSanctionsPage,
  BusinessOnboardingPage,
  CryptoPage,
  CustomerOnboardingPage,
  DevelopersPage,
  DocumentVerificationPage,
  ElectronicSignaturesPage,
  FinancialServicesPage,
  FraudPreventionPage,
  HrStaffingPage,
  IdentityLinkedSigningPage,
  KybVerificationPage,
  KycVerificationPage,
  MarketplacesPage,
  ProductPage,
  PublicDocsPage,
  PublicPricingPage,
  SecurityCompliancePage,
  SecurityPage,
  SelfieLivenessPage,
  SolutionsPage,
  SystemStatusPage,
} from './pages/MarketingPages';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import OperationalDashboard from './pages/OperationalDashboard';
import VerificationNew from './pages/VerificationNew';
import VerificationDetail from './pages/VerificationDetail';
import Settings from './pages/Settings';
import Docs from './pages/Docs';
import Usage from './pages/Usage';
import Billing from './pages/Billing';
import Integration from './pages/Integration';
import PublicVerify from './pages/PublicVerify';
import PublicSign from './pages/PublicSign';
import PublicSignatureVerify from './pages/PublicSignatureVerify';
import IdemRequest from './pages/IdemRequest';
import IdemActivate from './pages/IdemActivate';
import IdemAccount from './pages/IdemAccount';
import IdemRequestsInbox from './pages/IdemRequestsInbox';
import IdemSecurity from './pages/IdemSecurity';
import Signatures from './pages/Signatures';
import SignatureDetail from './pages/SignatureDetail';
import SignatureNew from './pages/SignatureNew';
import BusinessVerifications from './pages/BusinessVerifications';
import BusinessVerificationNew from './pages/BusinessVerificationNew';
import BusinessVerificationDetail from './pages/BusinessVerificationDetail';
import IdentityOperations from './pages/IdentityOperations';
import IdentityDetail from './pages/IdentityDetail';
import DigitalKeyAccounts from './pages/DigitalKeyAccounts';
import InternalAccounts from './pages/InternalAccounts';
import InternalAccountDetail from './pages/InternalAccountDetail';
import RelyingPartyProfiles from './pages/RelyingPartyProfiles';
import FasReadiness from './pages/FasReadiness';
import BosaEvidencePack from './pages/BosaEvidencePack';
import AuditGovernancePack from './pages/AuditGovernancePack';
import PartnerTechnicalReadiness from './pages/PartnerTechnicalReadiness';
import RegulatedIdentifiers from './pages/RegulatedIdentifiers';
import DigitalKeyArchitecture from './pages/DigitalKeyArchitecture';
import InternalAuthenticatorPreview from './pages/InternalAuthenticatorPreview';
import InternalDashboard from './pages/InternalDashboard';
import DigitalKeyDemoRunner from './pages/DigitalKeyDemoRunner';
import IdemAuthenticatorApp from './pages/IdemAuthenticatorApp';
import OidcLabSimulator from './pages/OidcLabSimulator';
import DashboardLayout from './components/DashboardLayout';
import InternalAdminLayout from './components/InternalAdminLayout';
import CookieConsentBanner from './components/CookieConsentBanner';
import { isPlatformAdminUser } from './lib/platformAdmin';

function authenticatedLandingPath(user) {
  return isPlatformAdminUser(user) ? '/internal' : '/dashboard';
}

function Protected({ children }) {
  const location = useLocation();
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const onboardingDone = user.onboarding_completed === true;
  if (!onboardingDone && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  if (onboardingDone && location.pathname === '/onboarding') {
    return <Navigate to={authenticatedLandingPath(user)} replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-zinc-500">Loading…</div>;
  if (user) return <Navigate to={authenticatedLandingPath(user)} replace />;
  return children;
}

function PlatformAdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-zinc-500">Loading…</div>;
  return isPlatformAdminUser(user) ? children : <Navigate to="/dashboard" replace />;
}

function LegacyInternalAdminRedirect() {
  const location = useLocation();
  const nextPath = location.pathname === '/admin' || location.pathname === '/admin/'
    ? '/internal'
    : location.pathname.replace(/^\/admin/, '/internal');
  return <Navigate to={`${nextPath}${location.search}${location.hash}`} replace />;
}

function ScrollToTopOnPublicRoutes() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (
      ['/', '/login', '/signup', '/pricing', '/docs', '/documentation', '/security', '/privacy', '/data-retention', '/status', '/system-status'].includes(pathname)
      || pathname.startsWith('/id/')
      || pathname.startsWith('/idem/')
      || pathname.startsWith('/product')
      || pathname.startsWith('/developers')
      || pathname.startsWith('/solutions')
      || pathname.startsWith('/use-cases')
      || pathname.startsWith('/industries')
      || pathname.startsWith('/resources')
    ) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTopOnPublicRoutes />
        <Routes>
          <Route path="/" element={<PublicOnly><Landing/></PublicOnly>}/>
          <Route path="/product" element={<ProductPage/>}/>
          <Route path="/product/KYC-verification" element={<KycVerificationPage/>}/>
          <Route path="/product/kyc-verification" element={<KycVerificationPage/>}/>
          <Route path="/product/selfie-liveness" element={<SelfieLivenessPage/>}/>
          <Route path="/product/age-verification" element={<AgeVerificationPage/>}/>
          <Route path="/product/electronic-signatures" element={<ElectronicSignaturesPage/>}/>
          <Route path="/product/document-verification" element={<DocumentVerificationPage/>}/>
          <Route path="/product/kyb-verification" element={<KybVerificationPage/>}/>
          <Route path="/product/aml-sanctions" element={<AmlSanctionsPage/>}/>
          <Route path="/product/:section" element={<Navigate to="/product" replace/>}/>
          <Route path="/developers" element={<DevelopersPage/>}/>
          <Route path="/developers/:section" element={<Navigate to="/developers" replace/>}/>
          <Route path="/use-cases/customer-onboarding" element={<CustomerOnboardingPage/>}/>
          <Route path="/use-cases/business-onboarding" element={<BusinessOnboardingPage/>}/>
          <Route path="/use-cases/fraud-prevention" element={<FraudPreventionPage/>}/>
          <Route path="/use-cases/identity-linked-signing" element={<IdentityLinkedSigningPage/>}/>
          <Route path="/industries/financial-services" element={<FinancialServicesPage/>}/>
          <Route path="/industries/crypto" element={<CryptoPage/>}/>
          <Route path="/industries/marketplaces" element={<MarketplacesPage/>}/>
          <Route path="/industries/hr-staffing" element={<HrStaffingPage/>}/>
          <Route path="/solutions" element={<SolutionsPage/>}/>
          <Route path="/solutions/:section" element={<Navigate to="/solutions" replace/>}/>
          <Route path="/security" element={<SecurityPage/>}/>
          <Route path="/resources/security-compliance" element={<SecurityCompliancePage/>}/>
          <Route path="/privacy" element={<Navigate to="/security" replace/>}/>
          <Route path="/data-retention" element={<Navigate to="/security" replace/>}/>
          <Route path="/status" element={<Navigate to="/security" replace/>}/>
          <Route path="/system-status" element={<SystemStatusPage/>}/>
          <Route path="/pricing" element={<PublicPricingPage/>}/>
          <Route path="/docs" element={<PublicDocsPage/>}/>
          <Route path="/documentation" element={<PublicDocsPage/>}/>
          <Route path="/login" element={<PublicOnly><Login/></PublicOnly>}/>
          <Route path="/signup" element={<PublicOnly><Signup/></PublicOnly>}/>
          <Route path="/individuals" element={<Navigate to="/dashboard/verifications" replace/>}/>
          <Route path="/documents" element={<Navigate to="/dashboard/signatures" replace/>}/>
          <Route path="/businesses" element={<Navigate to="/dashboard/businesses" replace/>}/>
          <Route path="/v/:token" element={<PublicVerify/>}/>
          <Route path="/sign/:token" element={<PublicSign/>}/>
          <Route path="/verify/signature/:verificationId" element={<PublicSignatureVerify/>}/>
          <Route path="/idem" element={<Navigate to="/idem/account" replace/>}/>
          <Route path="/idem/activate/:token" element={<IdemActivate/>}/>
          <Route path="/idem/account" element={<IdemAccount/>}/>
          <Route path="/idem/requests" element={<IdemRequestsInbox/>}/>
          <Route path="/idem/security" element={<IdemSecurity/>}/>
          <Route path="/idem/requests/:token" element={<IdemRequest/>}/>
          <Route path="/onboarding" element={<Protected><Onboarding/></Protected>}/>
          <Route path="/id/authenticator" element={<Protected><PlatformAdminOnly><IdemAuthenticatorApp/></PlatformAdminOnly></Protected>}/>

          <Route element={<Protected><DashboardLayout/></Protected>}>
            <Route path="/dashboard" element={<OperationalDashboard/>}/>
            <Route path="/dashboard/verifications" element={<Dashboard/>}/>
            <Route path="/dashboard/verifications/new" element={<VerificationNew/>}/>
            <Route path="/dashboard/verifications/:id" element={<VerificationDetail/>}/>
            <Route path="/dashboard/signatures" element={<Signatures/>}/>
            <Route path="/dashboard/signatures/new" element={<SignatureNew/>}/>
            <Route path="/dashboard/signatures/:signatureId" element={<SignatureDetail/>}/>
            <Route path="/dashboard/businesses" element={<BusinessVerifications/>}/>
            <Route path="/dashboard/businesses/new" element={<BusinessVerificationNew/>}/>
            <Route path="/dashboard/businesses/:id" element={<BusinessVerificationDetail/>}/>
            <Route path="/dashboard/integration" element={<Integration/>}/>
            <Route path="/dashboard/docs" element={<Docs/>}/>
            <Route path="/verifications/:id" element={<VerificationDetail/>}/>
            <Route path="/usage" element={<Usage/>}/>
            <Route path="/billing" element={<Billing/>}/>
            <Route path="/settings" element={<Settings/>}/>
          </Route>

          <Route path="/internal" element={<Protected><PlatformAdminOnly><InternalAdminLayout/></PlatformAdminOnly></Protected>}>
            <Route index element={<InternalDashboard/>}/>
            <Route path="accounts" element={<InternalAccounts/>}/>
            <Route path="accounts/:organizationId" element={<InternalAccountDetail/>}/>
            <Route path="identities" element={<IdentityOperations/>}/>
            <Route path="identities/:id" element={<IdentityDetail/>}/>
            <Route path="digital-key-accounts" element={<DigitalKeyAccounts/>}/>
            <Route path="relying-party-profiles" element={<RelyingPartyProfiles/>}/>
            <Route path="fas-readiness" element={<FasReadiness/>}/>
            <Route path="bosa-evidence-pack" element={<BosaEvidencePack/>}/>
            <Route path="audit-governance-pack" element={<AuditGovernancePack/>}/>
            <Route path="partner-technical-readiness" element={<PartnerTechnicalReadiness/>}/>
            <Route path="regulated-identifiers" element={<RegulatedIdentifiers/>}/>
            <Route path="digital-key-architecture" element={<DigitalKeyArchitecture/>}/>
            <Route path="digital-key-demo" element={<DigitalKeyDemoRunner/>}/>
            <Route path="authenticator-preview" element={<InternalAuthenticatorPreview/>}/>
            <Route path="oidc-lab" element={<OidcLabSimulator/>}/>
            <Route path="oidc-lab/callback" element={<OidcLabSimulator/>}/>
          </Route>

          <Route path="/admin" element={<Protected><PlatformAdminOnly><LegacyInternalAdminRedirect/></PlatformAdminOnly></Protected>}/>
          <Route path="/admin/*" element={<Protected><PlatformAdminOnly><LegacyInternalAdminRedirect/></PlatformAdminOnly></Protected>}/>

          <Route path="*" element={<Navigate to="/" replace/>}/>
        </Routes>
        <CookieConsentBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}
